import { signInWithCustomToken, onAuthStateChanged, signOut as firebaseSignOut, type Unsubscribe } from 'firebase/auth'
import { ref, type Ref } from 'vue'
import { CPFirebaseCredentialsGateway } from '../gateways/CPFirebaseCredentialsGateway'
import { useFirebase } from '~/shared/services/firebase'
import { useBugsnag } from '#imports'

const REFRESH_MARGIN_SECONDS = 300
const MAX_RETRY_ATTEMPTS = 5
const INITIAL_RETRY_SECONDS = 60

const isAuthenticated = ref(false)
const uuid = ref<string | null>(null)
const error = ref<Error | null>(null)
let refreshTimer: ReturnType<typeof setTimeout> | null = null
let authStateUnsubscribe: Unsubscribe | null = null
let authPromise: Promise<void> | null = null
let retryCount = 0

function calculateSecondsUntilExpiry (expiresIn: number): number {
  const now = Math.floor(Date.now() / 1000)

  if (expiresIn > now) {
    return expiresIn - now
  }

  return expiresIn
}

function getRetryDelay (): number {
  const delay = INITIAL_RETRY_SECONDS * Math.pow(2, retryCount)
  return Math.min(delay, 900)
}

function scheduleRefresh (expiresIn: number): void {
  clearRefreshTimer()

  const secondsUntilExpiry = calculateSecondsUntilExpiry(expiresIn)
  const refreshInMs = Math.max((secondsUntilExpiry - REFRESH_MARGIN_SECONDS), 60) * 1000

  refreshTimer = setTimeout(() => {
    authenticate(true)
  }, refreshInMs)
}

function scheduleRetry (): void {
  clearRefreshTimer()

  if (retryCount >= MAX_RETRY_ATTEMPTS) {
    const bugsnag = useBugsnag()
    bugsnag?.notify(new Error('Firebase auth max retry attempts reached'), (event) => {
      event.addMetadata('firebase', { retryCount })
    })
    return
  }

  const delaySeconds = getRetryDelay()
  retryCount++

  refreshTimer = setTimeout(() => {
    authenticate(true)
  }, delaySeconds * 1000)
}

function clearRefreshTimer (): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

async function authenticate (isRefresh = false): Promise<void> {
  const firebase = useFirebase()

  if (!firebase.auth) {
    error.value = new Error('Firebase Auth is not initialized')
    return
  }

  const result = await CPFirebaseCredentialsGateway.instance().getToken()

  if (!result.isOk()) {
    const gatewayError = result.getError()
    const bugsnag = useBugsnag()
    const authError = new Error('Failed to get Firebase credentials from backend')
    bugsnag?.notify(authError, (event) => {
      event.addMetadata('firebase', { isRefresh, errorCode: gatewayError?.code, errorMessage: gatewayError?.message })
    })
    error.value = authError

    scheduleRetry()

    return
  }

  const credentials = result.getValue()

  try {
    await signInWithCustomToken(firebase.auth, credentials.accessToken)
    isAuthenticated.value = true
    uuid.value = credentials.uuid
    error.value = null
    retryCount = 0
    scheduleRefresh(credentials.expiresIn)
  } catch (e) {
    const bugsnag = useBugsnag()
    bugsnag?.notify(e as Error, (event) => {
      event.addMetadata('firebase', { message: 'signInWithCustomToken failed', isRefresh })
    })
    error.value = e as Error
    isAuthenticated.value = false

    scheduleRetry()
  }
}

function setupAuthStateListener (): void {
  if (authStateUnsubscribe) {
    return
  }

  const firebase = useFirebase()
  if (!firebase.auth) {
    return
  }

  authStateUnsubscribe = onAuthStateChanged(firebase.auth, (user) => {
    if (!user && isAuthenticated.value) {
      isAuthenticated.value = false
      uuid.value = null
    }
  })
}

export interface FirebaseAuth {
  isAuthenticated: Ref<boolean>
  uuid: Ref<string | null>
  error: Ref<Error | null>
  ensureAuthenticated: () => Promise<void>
  signOut: () => Promise<void>
}

export function useFirebaseAuth (): FirebaseAuth {
  function ensureAuthenticated (): Promise<void> {
    if (isAuthenticated.value) {
      return Promise.resolve()
    }

    if (authPromise) {
      return authPromise
    }

    authPromise = authenticate().finally(() => {
      authPromise = null
    })

    setupAuthStateListener()

    return authPromise
  }

  async function signOut (): Promise<void> {
    clearRefreshTimer()
    retryCount = 0

    if (authStateUnsubscribe) {
      authStateUnsubscribe()
      authStateUnsubscribe = null
    }

    try {
      const firebase = useFirebase()
      if (firebase.auth) {
        await firebaseSignOut(firebase.auth)
      }
    } catch (e) {
      const bugsnag = useBugsnag()
      bugsnag?.notify(e as Error, (event) => {
        event.addMetadata('firebase', { message: 'Firebase signOut failed' })
      })
    }

    isAuthenticated.value = false
    uuid.value = null
    error.value = null
    authPromise = null
  }

  return {
    isAuthenticated,
    uuid,
    error,
    ensureAuthenticated,
    signOut
  }
}
